import React, { useEffect, useRef } from "react";
import {
  Book,
  Briefcase,
  FileText,
  Globe,
  LayoutTemplate,
  Paperclip,
  Search,
  type LucideIcon
} from "lucide-react";

import type { PlaygroundFileUpload, PlaygroundMessage } from "../types";
import { MessageBubbleAssistant } from "./MessageBubbleAssistant";
import { MessageBubbleUser } from "./MessageBubbleUser";
import { useAppTabsStore } from "../../appTabs/context/appTabsStore";
import { caseTabId } from "../../appTabs/tabRoutes";
import { parseTimestamp } from "../utils/time";
import { stripWritingBlock } from "../utils/stripWritingBlock";
import { DRAFT_TYPE_LABELS, DraftType } from "../../../api/types/draft";

type Props = {
  messages: PlaygroundMessage[];
  fileUploadsById: Record<string, PlaygroundFileUpload>;
  /** Enables per-message "Save to draft" on archived replies. */
  onSaveAssistantToDraft?: (content: string) => void;
  /** Enables per-message "Download" (Word/PDF) of the reply content. */
  onDownloadAssistant?: (content: string, format: "docx" | "pdf") => void;
};

// Types whose title carries the real name (e.g. a custom draft or an expert's
// letter), so we keep the snapshot title rather than the generic type label.
const TITLE_OVER_TYPE_LABEL = new Set<string>([
  DraftType.CUSTOM,
  DraftType.EXPERT_LETTER
]);

/**
 * The draft label for the turn this message belongs to, captured at send time.
 * Prefers the stable draft *type* (e.g. "Petitioner Support Letter") so older
 * turns don't re-label when the attached draft's title later changes; falls back
 * to the snapshot title for custom/expert drafts and for older messages that
 * predate the stored type.
 */
function getMessageAttachedDraftLabel(msg: PlaygroundMessage): string | null {
  const raw = (
    msg.metadata as
      | { attachedDraft?: { label?: unknown; draftType?: unknown } }
      | null
      | undefined
  )?.attachedDraft;
  if (!raw || typeof raw !== "object") return null;
  const label = (raw.label ?? "").toString().trim();
  const draftType = (raw.draftType ?? "").toString().trim();
  if (draftType && !TITLE_OVER_TYPE_LABEL.has(draftType)) {
    const typeLabel = DRAFT_TYPE_LABELS[draftType as DraftType];
    if (typeLabel) return typeLabel;
  }
  return label || null;
}

/** User bubbles replay raw API strings; may include pre-split `AAO Decisions`. */
function iconForSource(source: string): LucideIcon {
  if (source === "Case Context") return Briefcase;
  if (source === "USCIS Policy Manual") return Book;
  if (source === "AAO Precedent Decisions") return FileText;
  if (source === "AAO Adopted Decisions") return FileText;
  if (source === "AAO Non-Precedent Decisions") return FileText;
  if (source === "BIA Precedent Decisions") return FileText;
  if (source === "AAO Decisions") return FileText;
  if (source === "USCIS Web Search") return Globe;
  if (source === "Government Web Search") return Globe;
  if (source === "ONET Codes") return Briefcase;
  return Search; // General Web Search
}

function dateKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateDivider(date: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (dateKey(date) === dateKey(today)) return "Today";
  if (dateKey(date) === dateKey(yesterday)) return "Yesterday";

  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

function DateDivider({ date }: { date: Date }) {
  return (
    <div className="flex w-full items-center gap-3">
      <div className="h-px min-w-0 flex-1 bg-line-2" />
      <span className="flex-none text-sm text-fg-3">
        {formatDateDivider(date)}
      </span>
      <div className="h-px min-w-0 flex-1 bg-line-2" />
    </div>
  );
}

function MessageListComponent({
  messages,
  fileUploadsById,
  onSaveAssistantToDraft,
  onDownloadAssistant
}: Props) {
  const openTabs = useAppTabsStore((s) => s.tabs);
  const getSourcesUsedFromMetadata = (
    metadata: PlaygroundMessage["metadata"]
  ): string[] | null => {
    if (!metadata || typeof metadata !== "object") return null;
    const raw = (metadata as { data_sources?: unknown }).data_sources;
    if (!Array.isArray(raw)) return null;
    const out = raw.filter((s): s is string => typeof s === "string");
    return out.length ? out : null;
  };
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  return (
    <div className="flex w-full flex-col items-start gap-8">
      {messages.map((m, idx) => {
        const messageDate = parseTimestamp(m.created_at);
        const previousDate =
          idx > 0 ? parseTimestamp(messages[idx - 1]?.created_at) : null;
        const showDateDivider = Boolean(
          messageDate &&
            (!previousDate || dateKey(messageDate) !== dateKey(previousDate))
        );

        let messageNode: React.ReactNode;
        if (m.role === "user") {
          const attachments: { label: string; icon: LucideIcon }[] = (
            m.file_upload_ids ?? []
          ).map((id) => {
            const fu = fileUploadsById[id];
            return {
              label: fu?.filename ?? id,
              icon: Paperclip
            };
          });

          const pendingFileNames =
            (m.metadata as { pendingFiles?: unknown } | null | undefined)
              ?.pendingFiles ?? null;
          if (Array.isArray(pendingFileNames)) {
            pendingFileNames.forEach((name) => {
              if (typeof name !== "string") return;
              attachments.push({
                label: name,
                icon: Paperclip
              });
            });
          }

          const rawSelectedSources = m.selected_sources ?? null;
          const sources: { label: string; icon: LucideIcon }[] = [];

          const rawCaseContext =
            (m.metadata as { caseContext?: unknown } | null | undefined)
              ?.caseContext ?? null;
          if (rawCaseContext && typeof rawCaseContext === "object") {
            const cc = rawCaseContext as {
              enabled?: unknown;
              individualId?: unknown;
            };
            if (cc.enabled && typeof cc.individualId === "string") {
              const c = openTabs.find(
                (x) => x.id === caseTabId(cc.individualId as string)
              );
              const name = (c?.title ?? "").trim();
              const visa = (c?.workflow ?? "").trim();
              const label =
                name && visa ? `${name} – ${visa}` : name || visa || "Case";
              sources.push({ label, icon: Briefcase });
            }
          }

          const rawAttachedDraft =
            (m.metadata as { attachedDraft?: unknown } | null | undefined)
              ?.attachedDraft ?? null;
          if (rawAttachedDraft && typeof rawAttachedDraft === "object") {
            const label = (
              (rawAttachedDraft as { label?: unknown }).label ?? ""
            ).toString();
            if (typeof label === "string" && label.trim()) {
              sources.push({
                label: label.trim(),
                icon: FileText
              });
            }
          }

          const rawAttachedTemplate =
            (m.metadata as { attachedTemplate?: unknown } | null | undefined)
              ?.attachedTemplate ?? null;
          if (rawAttachedTemplate && typeof rawAttachedTemplate === "object") {
            const name = (
              (rawAttachedTemplate as { name?: unknown }).name ?? ""
            ).toString();
            if (typeof name === "string" && name.trim()) {
              sources.push({
                label: `Template/Sample - ${name.trim()}`,
                icon: LayoutTemplate
              });
            }
          }

          if (Array.isArray(rawSelectedSources)) {
            rawSelectedSources.forEach((s) => {
              if (typeof s !== "string") return;
              if (s === "Case Context") return;
              sources.push({ label: s, icon: iconForSource(s) });
            });
          }

          messageNode = (
            <MessageBubbleUser
              content={m.content}
              attachments={attachments}
              sources={sources}
            />
          );
        } else {
          // assistant + system messages render like assistant bubbles
          // Strip any ":::writing" container fences the model leaked, so the
          // reply is shown/saved/downloaded clean.
          const assistantContent = stripWritingBlock(m.content);
          // A turn that had a draft attached (stored on the user message that
          // started it) renders as a document card labelled with that draft.
          const precedingUserMessage = messages
            .slice(0, idx)
            .reverse()
            .find((mm) => mm.role === "user");
          const turnDraftLabel = precedingUserMessage
            ? getMessageAttachedDraftLabel(precedingUserMessage)
            : null;
          messageNode = (
            <MessageBubbleAssistant
              content={assistantContent}
              variant={turnDraftLabel ? "draft" : "chat"}
              sourcesUsed={getSourcesUsedFromMetadata(m.metadata)}
              canSaveToDraft={Boolean(onSaveAssistantToDraft)}
              saveToDraftLabel={turnDraftLabel}
              onSaveToDraft={
                onSaveAssistantToDraft
                  ? () => onSaveAssistantToDraft(assistantContent)
                  : undefined
              }
              canDownload={Boolean(onDownloadAssistant)}
              onDownload={
                onDownloadAssistant
                  ? (format) => onDownloadAssistant(assistantContent, format)
                  : undefined
              }
            />
          );
        }

        return (
          <React.Fragment key={m.playground_message_id}>
            {showDateDivider && messageDate ? (
              <DateDivider date={messageDate} />
            ) : null}
            {messageNode}
          </React.Fragment>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

// Memoized so parent re-renders (modals opening, etc.) don't re-parse every
// bubble's markdown. Relies on stable props from the parent (notably a
// memoized `onSaveAssistantToDraft`).
export const MessageList = React.memo(MessageListComponent);
